import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useMobileDetect } from '@/hooks';

export const OrientationWarning = () => {
  const [isLandscape, setIsLandscape] = useState(false);
  const isMobileDevice = useMobileDetect();

  useEffect(() => {
    const checkOrientation = () => {
      // Check if device is in landscape mode and is mobile size
      const isLandscapeMode =
        isMobileDevice &&
        window.innerHeight < window.innerWidth &&
        window.innerWidth < 1024;
      setIsLandscape(isLandscapeMode);
    };

    // Check on mount
    checkOrientation();

    // Listen for orientation changes
    window.addEventListener('resize', checkOrientation);
    window.addEventListener('orientationchange', checkOrientation);

    return () => {
      window.removeEventListener('resize', checkOrientation);
      window.removeEventListener('orientationchange', checkOrientation);
    };
  }, [isMobileDevice]);

  return (
    <AnimatePresence>
      {isLandscape && (
        <motion.div
          className="fixed inset-0 z-[9999] flex items-center justify-center bg-gradient-to-br from-indigo-800/95 via-purple-700/90 to-cyan-700/85 backdrop-blur-md"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
        >
          <motion.div
            className="relative bg-black/20 backdrop-blur-sm border-2 border-white/40 rounded-2xl px-8 py-6 max-w-[85vw] shadow-2xl"
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ delay: 0.1 }}
          >
            <div className="flex items-center gap-6">
              {/* Animated Tower Icon */}
              <motion.div
                className="flex-shrink-0"
                animate={{
                  y: [0, -4, 0],
                }}
                transition={{
                  duration: 2,
                  repeat: Infinity,
                  ease: 'easeInOut',
                }}
              >
                <TowerDefenseIcon />
              </motion.div>

              {/* Content */}
              <div className="flex-1">
                <motion.h1
                  className="font-lilita text-2xl text-white text-center mb-2"
                  style={{
                    textShadow: '2px 2px 4px rgba(0,0,0,0.8)',
                  }}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.2 }}
                >
                  🛡️ Commander! Rotate Device! ⚔️
                </motion.h1>
                <motion.p
                  className="font-lilita text-base text-white/90 text-center"
                  style={{
                    textShadow: '1px 1px 2px rgba(0,0,0,0.8)',
                  }}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.3 }}
                >
                  Your towers defend better in portrait! 🎯
                </motion.p>
              </div>

              {/* Rotating Phone Icon */}
              <motion.div
                className="flex-shrink-0"
                animate={{
                  rotate: [0, -90, -90, 0],
                }}
                transition={{
                  duration: 2.5,
                  repeat: Infinity,
                  repeatDelay: 0.8,
                  ease: 'easeInOut',
                }}
              >
                <svg
                  width="50"
                  height="50"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <rect
                    x="7"
                    y="3"
                    width="10"
                    height="18"
                    rx="2"
                    stroke="white"
                    strokeWidth="2"
                    fill="white"
                    fillOpacity="0.3"
                  />
                  <circle cx="12" cy="19" r="1" fill="white" />
                </svg>
              </motion.div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

const TowerDefenseIcon = () => (
  <svg
    width="50"
    height="50"
    viewBox="0 0 100 100"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    {/* Defense Shield */}
    <motion.path
      d="M50 15 L25 28 L25 50 Q25 68 50 78 Q75 68 75 50 L75 28 Z"
      stroke="white"
      strokeWidth="3"
      fill="white"
      fillOpacity="0.2"
      animate={{
        opacity: [0.6, 1, 0.6],
      }}
      transition={{
        duration: 2,
        repeat: Infinity,
        ease: 'easeInOut',
      }}
    />

    {/* Tower */}
    <g opacity="0.9">
      <rect x="45" y="48" width="10" height="16" fill="white" />
      <polygon points="45,48 50,40 55,48" fill="white" />
    </g>
  </svg>
);
